import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { FormNavigation } from '../components/form/FormNavigation';
import { CurrencyInput } from '../components/fields/CurrencyInput';
import { RadioGroup } from '../components/fields/RadioGroup';
import { useFormStore, useNavigation } from '../store/formStore';
import { simulationStepSchema } from '../utils/validation';

interface SimulationFormData {
  amount: number;
  project_type: string;
}

export function SimulationStep() {
  const { formData, updateField, errors } = useFormStore();
  const { goToPreviousStep, goToNextStep, canGoBack } = useNavigation();
  
  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors: formErrors },
    trigger
  } = useForm<SimulationFormData>({
    resolver: zodResolver(simulationStepSchema),
    defaultValues: {
      amount: formData.amount || 0,
      project_type: formData.project_type || ''
    },
    mode: 'onChange'
  });
  
  const watchedAmount = watch('amount');
  const watchedProjectType = watch('project_type');
  
  // Montants suggérés si non multiple de 500
  const isInvalidAmount = watchedAmount > 0 && watchedAmount % 500 !== 0;
  const suggestedLow = isInvalidAmount ? Math.max(500, Math.floor(watchedAmount / 500) * 500) : null;
  const suggestedHigh = isInvalidAmount ? Math.min(35000, Math.ceil(watchedAmount / 500) * 500) : null;

  // Logique de validation custom
  const canGoNext = !!(watchedAmount >= 500 &&
                       watchedAmount <= 35000 &&
                       watchedAmount % 500 === 0 &&
                       watchedProjectType &&
                       ['travaux', 'besoin', 'auto', 'conso'].includes(watchedProjectType));
  
  
  const projectOptions = [
    { value: 'travaux', label: 'Travaux maison' },
    { value: 'besoin', label: "Besoin d'argent" },
    { value: 'auto', label: 'Auto / moto' },
    { value: 'conso', label: 'Consommation' }
  ];
  
  const onSubmit = (data: SimulationFormData) => {
    // Mettre à jour les données du store
    Object.entries(data).forEach(([key, value]) => {
      updateField(key, value);
    });
    
    // Passer à l'étape suivante
    goToNextStep();
  };
  
  const handleAmountChange = async (value: number) => {
    setValue('amount', value, { shouldValidate: true });
    updateField('amount', value);
    await trigger('amount'); // Force validation
  };
  
  const handleProjectTypeChange = async (value: string) => {
    setValue('project_type', value, { shouldValidate: true });
    updateField('project_type', value);
    await trigger('project_type'); // Force validation
  };
  
  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Simulation de votre crédit</CardTitle>
          <CardDescription>
            Commençons par définir votre projet et le montant souhaité
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Montant */}
            <div>
              <CurrencyInput
                label="Combien souhaitez-vous emprunter ?"
                value={watchedAmount}
                onChange={handleAmountChange}
                min={500}
                max={35000}
                step={100}
                required
                error={
                formErrors.amount?.message && formErrors.amount.message !== 'Le montant doit être un multiple de 500 €'
                  ? formErrors.amount.message
                  : errors.amount
              }
                helperText="Montant entre 500 € et 35 000 €, par paliers de 500 €"
              />
              {isInvalidAmount && suggestedLow !== null && suggestedHigh !== null && (
                <div className="mt-2 p-3 bg-warning-50 border border-warning-200 rounded-lg text-sm">
                  <p className="text-warning-700 mb-2">
                    Ce montant n'est pas disponible. Choisissez l'un de ces montants :
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); handleAmountChange(suggestedLow!); }}
                      className="px-4 py-1.5 bg-white border border-warning-400 text-warning-800 font-medium rounded-lg hover:bg-warning-50 transition-colors"
                    >
                      {suggestedLow.toLocaleString('fr-FR')} €
                    </button>
                    {suggestedHigh !== suggestedLow && (
                      <button
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); handleAmountChange(suggestedHigh!); }}
                        className="px-4 py-1.5 bg-white border border-warning-400 text-warning-800 font-medium rounded-lg hover:bg-warning-50 transition-colors"
                      >
                        {suggestedHigh.toLocaleString('fr-FR')} €
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Type de projet */}
            <div>
              <RadioGroup
                label="Quel est votre projet ?"
                options={projectOptions}
                value={watchedProjectType}
                onChange={handleProjectTypeChange}
                name="project_type"
                required
                error={formErrors.project_type?.message || errors.project_type}
              />
            </div>
            
            {/* Affichage conditionnel selon le montant */}
            {watchedAmount > 0 && (
              <div className="bg-success-50 border border-success-200 rounded-lg p-4">
                <div className="text-sm text-success-700">
                  <strong>Estimation rapide :</strong>
                  <br />
                  Montant : <span className="font-semibold">{watchedAmount.toLocaleString('fr-FR')} €</span>
                  {watchedProjectType && (
                    <>
                      <br />
                      Projet : <span className="font-semibold">
                        {projectOptions.find(opt => opt.value === watchedProjectType)?.label}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}
            
            <FormNavigation
              onPrevious={canGoBack ? goToPreviousStep : undefined}
              onNext={handleSubmit(onSubmit)}
              canGoBack={canGoBack}
              canGoNext={canGoNext}
              nextButtonText="Continuer"
            />
          </form>
        </CardContent>
      </Card>
    </div>
  );
}